import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import { grid } from "ldrs";
import { Spinner } from "react-bootstrap";
import Chatbot from "../chatbot/chatbot";
import ManageFile from "../chatbot/ManageFile";
// import SubQuestionsModal from "../../../component/SubQuestionsModal"; // DISABLED: Subquestions functionality disabled
import ApiHelpOfferModal from "../../../component/ApiHelpOfferModal";
import ApiQuestionsModal from "../../../component/ApiQuestionsModal";
import FillInBlanksAnswerField, { countFillInBlanks, effectiveUnitsArray } from "./FillInBlanksAnswerField";
import {
  FaRobot, FaFileUpload, FaArrowLeft, FaArrowRight,
  FaCheckCircle, FaClock, FaTimesCircle, FaLightbulb,
  FaQuestionCircle, FaBookOpen, FaChartLine, FaSave,
  FaChevronLeft, FaChevronRight, FaPlay, FaStop,
  FaLeaf, FaTree, FaRecycle, FaWater, FaWind, FaSolarPanel,
  FaUsers, FaHandshake, FaShieldAlt, FaBalanceScale,
  FaChartBar, FaTrendingUp, FaBullseye, FaCrosshairs,
  FaGlobe, FaHeart, FaStar, FaGem, FaRocket, FaMagic, FaCalculator
} from "react-icons/fa";

grid.register();

const AnswersToQuestions = () => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Helper function to get translated question text
  const getQuestionText = (question) => {
    if (!question) return '';
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    const isSpanish = currentLanguage === 'es' || currentLanguage.startsWith('es');

    if (isSpanish && question.engagementQuestionEs) {
      return question.engagementQuestionEs;
    }
    return question.engagementQuestion || '';
  };
  const [answers, setAnswers] = useState({});
  const [noAnswerReasons, setNoAnswerReasons] = useState({});
  const [answerTypes, setAnswerTypes] = useState({}); // New state for answer types
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [questionAnswered, setQuestionAnswered] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const topic = searchParams.get("topic");
  const questionIdFromUrl = searchParams.get("questionId");
  const [isFileModalOpen, setIsFileModalOpen] = useState(false); // State for file modal visibility
  const [showESGTips, setShowESGTips] = useState(false);
  // const [showSubQuestionsModal, setShowSubQuestionsModal] = useState(false); // DISABLED: Subquestions functionality disabled
  // const [currentQuestionForSubQuestions, setCurrentQuestionForSubQuestions] = useState(null); // DISABLED: Subquestions functionality disabled
  const [showApiHelpOfferModal, setShowApiHelpOfferModal] = useState(false);
  const [showApiQuestionsModal, setShowApiQuestionsModal] = useState(false);
  const [currentQuestionForApi, setCurrentQuestionForApi] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false); // State for question transition effect
  const [showCompletionModal, setShowCompletionModal] = useState(false); // State to control completion modal

  // ESG Category Icons and Colors
  const getESGCategoryInfo = (disclosureTopic, sustainableDimension) => {
    // First check the sustainableDimension (e.g., "Social Capital", "Human Capital", "Environment")
    const dimensionLower = (sustainableDimension || '').toLowerCase();
    const topicLower = (disclosureTopic || '').toLowerCase();

    // Check dimension first (more reliable)
    if (dimensionLower.includes('environment')) {
      return {
        icon: FaTree,
        color: 'green',
        category: 'Environmental',
        bgColor: 'from-green-50 to-emerald-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-800'
      };
    } else if (dimensionLower.includes('social capital') || dimensionLower.includes('human capital')) {
      return {
        icon: FaUsers,
        color: 'blue',
        category: 'Social',
        bgColor: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-800'
      };
    } else if (dimensionLower.includes('leadership') || dimensionLower.includes('governance')) {
      return {
        icon: FaShieldAlt,
        color: 'purple',
        category: 'Governance',
        bgColor: 'from-purple-50 to-violet-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-800'
      };
    }
    
    // Fallback to checking disclosureTopic if dimension is not available
    if (topicLower.includes('environment') || topicLower.includes('carbon') || topicLower.includes('energy') || topicLower.includes('waste') || topicLower.includes('water')) {
      return {
        icon: FaTree,
        color: 'green',
        category: 'Environmental',
        bgColor: 'from-green-50 to-emerald-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-800'
      };
    } else if (topicLower.includes('social') || topicLower.includes('employee') || topicLower.includes('community') || topicLower.includes('diversity') || topicLower.includes('human')) {
      return {
        icon: FaUsers,
        color: 'blue',
        category: 'Social',
        bgColor: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-800'
      };
    } else if (topicLower.includes('governance') || topicLower.includes('board') || topicLower.includes('ethics') || topicLower.includes('risk') || topicLower.includes('compliance')) {
      return {
        icon: FaShieldAlt,
        color: 'purple',
        category: 'Governance',
        bgColor: 'from-purple-50 to-violet-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-800'
      };
    } else {
      return {
        icon: FaChartBar,
        color: 'gray',
        category: 'ESG',
        bgColor: 'from-gray-50 to-slate-50',
        borderColor: 'border-gray-200',
        textColor: 'text-gray-800'
      };
    }
  };

  // Get unique categories from current questions
  const getActiveCategories = () => {
    const categorySet = new Set();
    questions.forEach(question => {
      const categoryInfo = getESGCategoryInfo(question.disclosureTopic, question.sustainableDimension);
      // Only include Environmental, Social, and Governance (exclude generic ESG)
      if (['Environmental', 'Social', 'Governance'].includes(categoryInfo.category)) {
        categorySet.add(categoryInfo.category);
      }
    });
    return Array.from(categorySet);
  };

  // Get category display info
  const getCategoryDisplayInfo = (categoryName) => {
    const categoryMap = {
      'Environmental': {
        icon: FaTree,
        color: 'green',
        bgColor: 'from-green-50 to-emerald-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-800',
        iconColor: 'text-green-600',
        labelColor: 'text-green-700'
      },
      'Social': {
        icon: FaUsers,
        color: 'blue',
        bgColor: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-800',
        iconColor: 'text-blue-600',
        labelColor: 'text-blue-700'
      },
      'Governance': {
        icon: FaShieldAlt,
        color: 'purple',
        bgColor: 'from-purple-50 to-violet-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-800',
        iconColor: 'text-purple-600',
        labelColor: 'text-purple-700'
      }
    };
    return categoryMap[categoryName] || null;
  };

  useEffect(() => {
    fetchQuestions();
  }, [topic]);

  // Handle navigation to specific question from URL parameter
  useEffect(() => {
    if (questions.length > 0 && questionIdFromUrl) {
      // Try to find question by ID (handle both string and number IDs)
      const questionIdNum = parseInt(questionIdFromUrl);
      const questionIndex = questions.findIndex(q => {
        // Compare as both string and number to handle different ID formats
        return q.id === questionIdNum || 
               q.id === questionIdFromUrl || 
               String(q.id) === String(questionIdFromUrl);
      });
      
      if (questionIndex !== -1) {
        setCurrentQuestionIndex(questionIndex);
        // Scroll to the question in sidebar after a short delay to ensure DOM is ready
        setTimeout(() => {
          const questionElement = document.querySelector(`[data-question-id="${questionIdFromUrl}"]`);
          if (questionElement) {
            questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight the question briefly
            questionElement.classList.add('ring-2', 'ring-green-500', 'ring-opacity-75');
            setTimeout(() => {
              questionElement.classList.remove('ring-2', 'ring-green-500', 'ring-opacity-75');
            }, 2000);
          }
        }, 300);
        // Remove questionId from URL to clean it up
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('questionId');
        const newUrl = newSearchParams.toString() 
          ? `${window.location.pathname}?${newSearchParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [questions, questionIdFromUrl, searchParams]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const endpoint = topic
        ? `/questions-for-answers-by-topic?topic=${encodeURIComponent(topic)}`
        : "/questions-for-answers";

      const response = await axios.get(endpoint);
      if (response.data.status) {
        // Normalize hasApiSupport to boolean for all questions
        const normalizedQuestions = (response.data.questionsAssignedToUser || []).map(q => {
          // Convert hasApiSupport to boolean explicitly
          const hasApiSupport = q.hasApiSupport === true || q.hasApiSupport === 'true' || q.hasApiSupport === 1 || q.hasApiSupport === '1';
          
          return {
            ...q,
            hasApiSupport: hasApiSupport,
            showAnswerStatusOptions: q.showAnswerStatusOptions !== false,
          };
        });
        
        setQuestions(normalizedQuestions);
        setQuestionCount(normalizedQuestions.length);

        const prefilledAnswers = normalizedQuestions.reduce((acc, question) => {
          let a = question.answer || '';
          if ((question.answerInputKind || 'text') === 'fill_in_blanks' && !a) {
            const expected = countFillInBlanks(question.fillInBlanksTemplate);
            a = JSON.stringify({
              blanks: Array(Math.max(0, expected)).fill(''),
              units: Array(Math.max(0, expected)).fill(''),
            });
          }
          acc[question.id] = a;
          return acc;
        }, {});
        
        setAnswers(prefilledAnswers);

        const prefilledNoAnswerReasons = normalizedQuestions.reduce((acc, question) => {
          acc[question.id] = question.section9_1 ? (question.noAnswerReason || '') : '';
          return acc;
        }, {});
        setNoAnswerReasons(prefilledNoAnswerReasons);

        // Add prefilled answer types
        const prefilledAnswerTypes = normalizedQuestions.reduce((acc, question) => {
          acc[question.id] = question.answerType;
          return acc;
        }, {});
        setAnswerTypes(prefilledAnswerTypes);
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toastr.error(t('answers.failedToFetchQuestions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (questions.length > 0 && questions[currentQuestionIndex]) {
      const currentQ = questions[currentQuestionIndex];
      
      localStorage.setItem("currentQuestion", JSON.stringify(currentQ));
      localStorage.setItem("currentQuestionId", currentQ.id);

      // Dispatch custom event to notify chatbot about question change
      window.dispatchEvent(new CustomEvent("questionChanged", {
        detail: { questionId: currentQ.id }
      }));
    }
  }, [questions, currentQuestionIndex]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNoAnswerReasonChange = (questionId, value) => {
    setNoAnswerReasons((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleAnswerTypeChange = (questionId, type) => {
    setAnswerTypes((prev) => ({ ...prev, [questionId]: type }));
  };

  // Handle subquestions modal save - DISABLED: Subquestions functionality disabled
  // const handleSubQuestionsSave = () => {
  //   setQuestionAnswered((prev) => prev + 1);
  //   if (currentQuestionIndex < questions.length - 1) {
  //     // Trigger transition effect
  //     setIsTransitioning(true);
  //     setTimeout(() => {
  //       setCurrentQuestionIndex((prev) => prev + 1);
  //       setTimeout(() => setIsTransitioning(false), 500);
  //     }, 200);
  //   } else {
  //     toastr.success(t('answers.allQuestionsAnswered'));
  //   }
  // };

  // Handle API help offer modal - user accepts
  const handleApiHelpAccept = () => {
    setShowApiHelpOfferModal(false);
    
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) {
      toastr.error(t('answers.questionDataNotAvailable'));
      return;
    }

    // Check if question has apiNumbers set
    if (currentQuestion.apiNumbers) {
      // Parse apiNumbers from comma-separated string (e.g., "1,2,3")
      const apiNumbersArray = currentQuestion.apiNumbers
        .split(',')
        .map(num => parseInt(num.trim()))
        .filter(num => !isNaN(num) && num >= 1 && num <= 12);
      
      if (apiNumbersArray.length > 0) {
        // Navigate to UnifiedApiComponent with API numbers
        const returnUrl = topic 
          ? `/questions-answers?topic=${encodeURIComponent(topic)}`
          : '/questions-answers';
        
        const apiNumbersParam = apiNumbersArray.join(',');
        navigate(`/api/unified?apis=${apiNumbersParam}&questionId=${currentQuestion.id}&userQuestionsAssignmentId=${currentQuestion.userQuestionsAssignment}&returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
    }

    // Fallback: Open API questions modal (old behavior)
    setShowApiQuestionsModal(true);
  };

  // Handle API help offer modal - user declines
  const handleApiHelpDecline = () => {
    setShowApiHelpOfferModal(false);
    setQuestionAnswered((prev) => prev + 1);
    if (currentQuestionIndex < questions.length - 1) {
      // Trigger transition effect
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        setTimeout(() => setIsTransitioning(false), 500);
      }, 200);
    } else {
      toastr.success(t('answers.allQuestionsAnswered'));
    }
  };

  // Handle API questions modal save/close
  const handleApiQuestionsSave = async () => {
    // Refresh questions to get updated answers
    await fetchQuestions();
    setShowApiQuestionsModal(false);
    setQuestionAnswered((prev) => prev + 1);
    if (currentQuestionIndex < questions.length - 1) {
      // Trigger transition effect
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        setTimeout(() => setIsTransitioning(false), 500);
      }, 200);
    } else {
      toastr.success(t('answers.allQuestionsAnswered'));
    }
  };

  const handleSaveAnswer = async (questionId, userQuestionsAssignmentId) => {
    setSaving(true);
    try {
      const currentQuestion = questions[currentQuestionIndex];
      const inputKind = currentQuestion?.answerInputKind || 'text';
      const showStatusOptions = currentQuestion?.showAnswerStatusOptions !== false;
      const isSection91 = Boolean(currentQuestion?.section9_1);
      const reasonTrim = isSection91
        ? (noAnswerReasons[questionId] ?? '').toString().trim()
        : '';

      let answerValue = answers[questionId];
      if (inputKind === 'fill_in_blanks') {
        let payload;
        try {
          payload = JSON.parse(answers[questionId] || '{}');
        } catch {
          toastr.error(t('answers.failedToSaveAnswer'));
          setSaving(false);
          return;
        }
        const expected = countFillInBlanks(currentQuestion.fillInBlanksTemplate);
        const unitOpts = currentQuestion.valueUnitFormat || [];
        const needUnit = Boolean(currentQuestion.answerIncludeUnitDropdown);
        if (isSection91 && reasonTrim) {
          const b = Array.isArray(payload.blanks) ? [...payload.blanks] : [];
          while (b.length < expected) b.push('');
          answerValue = JSON.stringify({
            blanks: b.slice(0, expected).map((x) => String(x ?? '').trim()),
            ...(needUnit
              ? {
                  units: effectiveUnitsArray(payload, expected).map((u) => String(u ?? '').trim()),
                }
              : {}),
          });
        } else {
          if (!payload.blanks || payload.blanks.length !== expected) {
            toastr.error(t('answers.fillInBlanksIncomplete'));
            setSaving(false);
            return;
          }
          for (let i = 0; i < expected; i++) {
            if (!String(payload.blanks[i] ?? '').trim()) {
              toastr.error(t('answers.fillInBlanksIncomplete'));
              setSaving(false);
              return;
            }
          }
          if (needUnit) {
            const unitArr = effectiveUnitsArray(payload, expected).map((u) => String(u ?? '').trim());
            if (unitArr.some((u) => !u || !unitOpts.includes(u))) {
              toastr.error(t('answers.selectUnitRequired'));
              setSaving(false);
              return;
            }
          }
          answerValue = JSON.stringify({
            blanks: payload.blanks.map((b) => String(b).trim()),
            ...(needUnit
              ? { units: effectiveUnitsArray(payload, expected).map((u) => String(u ?? '').trim()) }
              : {}),
          });
        }
      } else if (inputKind === 'number') {
        const raw = (answerValue ?? '').toString().trim();
        if (raw !== '' && Number.isFinite(Number(raw))) {
          answerValue = raw;
        } else if (reasonTrim) {
          answerValue = '';
        } else {
          toastr.error(t('answers.numericOrReasonRequired'));
          setSaving(false);
          return;
        }
      } else {
        if (showStatusOptions && !answerValue) {
          answerValue =
            answerTypes[questionId] === 'true'
              ? t('answers.yes')
              : answerTypes[questionId] === 'false'
                ? t('answers.no')
                : answerTypes[questionId] === 'progress'
                  ? t('answers.inProgress')
                  : '';
        }
        const detailTrim = (answerValue ?? '').toString().trim();
        if (!detailTrim && !reasonTrim) {
          toastr.error(t('answers.answerOrReasonRequired'));
          setSaving(false);
          return;
        }
      }

      const response = await axios.post("/submit-answer", {
        userQuestionsAssignmentId,
        questionId,
        answer: answerValue,
        answerType: showStatusOptions ? answerTypes[questionId] || null : null,
        noAnswerReason: reasonTrim || undefined,
      });

      if (response.data.status) {
        if (questions[currentQuestionIndex]) {
          localStorage.setItem("currentQuestion", JSON.stringify(questions[currentQuestionIndex]));
        }
        toastr.success(t('answers.answerSaved'));
        
        const currentQuestion = questions[currentQuestionIndex];
        
        if (!currentQuestion) {
          return;
        }

        // DISABLED: Subquestions functionality disabled
        // Check if answer is "true" and has subquestions - open modal
        // if (answerTypes[questionId] === 'true') {
        //   const hasSubQuestions = currentQuestion.subQuestions &&
        //     currentQuestion.subQuestions.length > 0;

        //   if (hasSubQuestions) {
        //     setCurrentQuestionForSubQuestions(currentQuestion);
        //     setShowSubQuestionsModal(true);
        //     return; // Don't move to next question yet
        //   }
        // }

        // For "false" or "progress" answers, just proceed to next question
        // No API navigation - user can access API help from SubQuestionsModal if needed

        setQuestionAnswered((prev) => prev + 1);
        if (currentQuestionIndex < questions.length - 1) {
          // Trigger transition effect
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentQuestionIndex((prev) => prev + 1);
            // The useEffect will handle the question change event dispatch
            setTimeout(() => setIsTransitioning(false), 500); // Reset transition state after animation
          }, 150);
        } else {
          // This is the last question - check if all questions are answered
          // Use the answerValue and answerType we just saved for the current question
          const questionHasFilledAnswer = (q, idJustSaved, savedVal, savedReason) => {
            const kind = q.answerInputKind || 'text';
            const r = q.section9_1
              ? (q.id === idJustSaved
                  ? (savedReason ?? '').toString().trim()
                  : (noAnswerReasons[q.id] ?? '').toString().trim())
              : '';
            if (r) return true;
            if (kind === 'fill_in_blanks') {
              const raw = q.id === idJustSaved ? savedVal : answers[q.id];
              try {
                const o = JSON.parse(raw || '{}');
                const exp = countFillInBlanks(q.fillInBlanksTemplate);
                if (!o.blanks || o.blanks.length !== exp) return false;
                if (o.blanks.some((b) => !String(b || '').trim())) return false;
                if (q.answerIncludeUnitDropdown) {
                  const opts = q.valueUnitFormat || [];
                  const unitArr = effectiveUnitsArray(o, exp);
                  if (unitArr.some((u) => !String(u || '').trim() || !opts.includes(String(u).trim())))
                    return false;
                }
                return true;
              } catch {
                return false;
              }
            }
            if (q.id === idJustSaved) {
              const s = (savedVal ?? '').toString().trim();
              if (kind === 'number') return s !== '' && Number.isFinite(Number(s));
              if (q.showAnswerStatusOptions === false) return s !== '';
              return s !== '' || (answerTypes[idJustSaved] && answerTypes[idJustSaved] !== 'progress');
            }
            const s = (answers[q.id] ?? '').toString().trim();
            if (kind === 'number') return s !== '' && Number.isFinite(Number(s));
            const hasAnswer = s !== '';
            const hasDefinitiveAnswer =
              q.showAnswerStatusOptions !== false &&
              answerTypes[q.id] &&
              answerTypes[q.id] !== 'progress';
            return hasAnswer || hasDefinitiveAnswer;
          };

          const allAnswered =
            questions.length > 0 &&
            questions.every((q) => questionHasFilledAnswer(q, questionId, answerValue, reasonTrim));
          
          if (allAnswered) {
            toastr.success(t('answers.allQuestionsAnswered'));
            setShowCompletionModal(true);
          }
        }
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error("Error saving answer:", error);
      toastr.error(t('answers.failedToSaveAnswer'));
    } finally {
      setSaving(false);
    }
  };

  const getAnswerTypeIcon = (type) => {
    switch (type) {
      case 'true': return <FaCheckCircle className="w-4 h-4 text-green-500" />;
      case 'false': return <FaTimesCircle className="w-4 h-4 text-red-500" />;
      case 'progress': return <FaClock className="w-4 h-4 text-yellow-500" />;
      default: return <FaQuestionCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAnswerTypeColor = (type) => {
    switch (type) {
      case 'true': return 'bg-green-50 border-green-200 text-green-800';
      case 'false': return 'bg-red-50 border-red-200 text-red-800';
      case 'progress': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  // Calculate progress based on answered questions (have text or definitive answer)
  const calculateProgress = () => {
    let answeredCount = 0;
    questions.forEach((question) => {
      const kind = question.answerInputKind || 'text';
      const raw = (answers[question.id] ?? '').toString().trim();
      const reason = question.section9_1
        ? (noAnswerReasons[question.id] ?? '').toString().trim()
        : '';
      const hasReason = reason !== '';
      if (kind === 'fill_in_blanks') {
        if (hasReason) {
          answeredCount++;
          return;
        }
        try {
          const o = JSON.parse(answers[question.id] || '{}');
          const exp = countFillInBlanks(question.fillInBlanksTemplate);
          if (
            o.blanks &&
            o.blanks.length === exp &&
            o.blanks.every((b) => String(b || '').trim()) &&
            (!question.answerIncludeUnitDropdown ||
              (() => {
                const opts = question.valueUnitFormat || [];
                const unitArr = effectiveUnitsArray(o, exp);
                return unitArr.every(
                  (u) => String(u || '').trim() !== '' && opts.includes(String(u).trim())
                );
              })())
          ) {
            answeredCount++;
          }
        } catch {
          /* incomplete */
        }
        return;
      }
      const hasNumeric =
        kind === 'number' && raw !== '' && Number.isFinite(Number(raw));
      const hasTextAnswer = kind !== 'number' && raw !== '';
      const hasDefinitiveAnswer =
        question.showAnswerStatusOptions !== false &&
        answerTypes[question.id] &&
        answerTypes[question.id] !== 'progress';
      if (hasNumeric || hasTextAnswer || hasDefinitiveAnswer || hasReason) {
        answeredCount++;
      }
    });
    return answeredCount;
  };

  const answeredQuestionsCount = calculateProgress();
  const progressPercentage = questionCount > 0 ? (answeredQuestionsCount / questionCount) * 100 : 0;
  const esgScore =
    questionCount > 0 ? Math.round((answeredQuestionsCount / questionCount) * 100) : 0;

  // ESG Tips for current question
  const getESGTips = (disclosureTopic) => {
    const topicLower = disclosureTopic?.toLowerCase() || '';

    if (topicLower.includes('environment') || topicLower.includes('carbon')) {
      return [
        "Consider your carbon footprint reduction initiatives",
        "Document renewable energy usage and targets",
        "Include waste reduction and recycling programs",
        "Mention water conservation measures"
      ];
    } else if (topicLower.includes('social') || topicLower.includes('employee')) {
      return [
        "Highlight diversity and inclusion programs",
        "Document employee training and development",
        "Include community engagement initiatives",
        "Mention health and safety improvements"
      ];
    } else if (topicLower.includes('governance') || topicLower.includes('board')) {
      return [
        "Describe board composition and diversity",
        "Document ethics and compliance programs",
        "Include risk management frameworks",
        "Mention transparency and reporting practices"
      ];
    }
    return [
      "Provide specific examples and metrics",
      "Include timelines and targets where applicable",
      "Reference relevant standards (GRI, SASB, TCFD)",
      "Document stakeholder engagement processes"
    ];
  };

  // Show loading state while fetching
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no questions found (after loading is complete)
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-2xl relative overflow-hidden">
          <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 relative z-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                <FaLeaf className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{t('answers.esgAssessmentPortal')}</h1>
                <p className="text-green-100 text-sm sm:text-base lg:text-lg flex items-center">
                  <FaGlobe className="w-4 h-4 mr-2" />
                  {t('answers.completeSustainabilityJourney')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center py-12 sm:py-20">
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <FaQuestionCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-600" />
            </div>
            {topic ? (
              <>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-700 mb-4">
                  {t('answers.noQuestionsForTopic', { topic }) || `No questions found for "${topic}"`}
                </h3>
                <p className="text-gray-500 text-lg mb-8">
                  {t('answers.noQuestionsForTopicDescription', { topic }) || `There are no questions assigned to you for the disclosure topic "${topic}".`}
                </p>
                <button
                  onClick={() => navigate('/questions-answers')}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200"
                >
                  {t('answers.viewAllQuestions') || 'View All Questions'}
                </button>
              </>
            ) : (
              <>
                <h3 className="text-3xl font-bold text-gray-700 mb-4">{t('answers.noEsgQuestionsAssigned')}</h3>
                <p className="text-gray-500 text-lg mb-8">You don't have any sustainability assessment questions assigned at the moment.</p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">Environmental</span>
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Social</span>
                  <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Governance</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Early return if current question doesn't exist (shouldn't happen, but safety check)
  if (!questions[currentQuestionIndex]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Enhanced Header Section */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-2xl relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 left-4 w-12 h-12 border-2 border-white rounded-full"></div>
          <div className="absolute top-8 right-8 w-8 h-8 bg-white rounded-full"></div>
          <div className="absolute bottom-4 left-12 w-10 h-10 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-8 right-4 w-6 h-6 bg-white rounded-full"></div>
            </div>
        
        <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                <FaLeaf className="w-10 h-10 text-white" />
            </div>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">{t('answers.esgAssessmentPortal')}</h1>
                <p className="text-green-100 text-lg flex items-center">
                  <FaGlobe className="w-4 h-4 mr-2" />
                  {t('answers.completeSustainabilityJourney')}
                </p>
        </div>
      </div>
  
            {/* Enhanced Progress Cards */}
            <div className="flex flex-col sm:flex-row gap-4 mt-4 sm:mt-0">
              {/* Progress Card */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <FaChartLine className="w-6 h-6 text-white" />
                  <span className="text-white font-semibold text-lg">{t('answers.progress')}</span>
                  </div>
                <div className="text-3xl font-bold text-white mb-2">{answeredQuestionsCount}/{questionCount}</div>
                <div className="w-40 bg-white/30 rounded-full h-3 mb-2">
                  <div
                    className="bg-gradient-to-r from-white to-green-200 h-3 rounded-full transition-all duration-1000 shadow-sm"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="text-sm text-green-100 font-medium">{Math.round(progressPercentage)}{t('answers.percentComplete')}</div>
                </div>

              {/* ESG Score Card */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <FaStar className="w-6 h-6 text-white" />
                  <span className="text-white font-semibold text-lg">{t('answers.esgScore')}</span>
                </div>
                <div className="text-3xl font-bold text-white mb-2">{esgScore}%</div>
                <div className="w-40 bg-white/30 rounded-full h-3 mb-2">
                  <div 
                    className="bg-gradient-to-r from-yellow-300 to-orange-300 h-3 rounded-full transition-all duration-1000 shadow-sm"
                    style={{ width: `${esgScore}%` }}
                  ></div>
                </div>
                <div className="text-sm text-green-100 font-medium">{t('answers.sustainabilityRating')}</div>
              </div>
            </div>
          </div>
              </div>
            </div>

      <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {loading ? (
          <div className="flex justify-center items-center py-12 sm:py-16">
            <div className="text-center">
              <l-grid size="80" speed="1.5" color="#10B981" />
              <p className="mt-4 text-base sm:text-lg text-gray-600">Loading questions...</p>
            </div>
          </div>
        ) : questions.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* Enhanced Question Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sticky top-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaQuestionCircle className="w-6 h-6 mr-3 text-green-600" />
                  ESG Questions
                </h3>
  
                {/* Dynamic ESG Categories Summary - Only show categories present in questions */}
                {(() => {
                  const activeCategories = getActiveCategories();
                  if (activeCategories.length > 0) {
                    // Determine background color based on categories present
                    const getCategoryBgColor = () => {
                      if (activeCategories.includes('Environmental') && activeCategories.length === 1) {
                        return 'from-green-50 to-emerald-50 border-green-200';
                      } else if (activeCategories.includes('Social') && activeCategories.length === 1) {
                        return 'from-blue-50 to-indigo-50 border-blue-200';
                      } else if (activeCategories.includes('Governance') && activeCategories.length === 1) {
                        return 'from-purple-50 to-violet-50 border-purple-200';
                      }
                      return 'from-green-50 to-emerald-50 border-green-200';
                    };

                    const bgClass = getCategoryBgColor();
                    const colsClass = activeCategories.length === 1 ? 'grid-cols-1' : activeCategories.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

                    return (
                      <div className={`mb-6 p-4 bg-gradient-to-r ${bgClass} rounded-xl border`}>
                        <h4 className="text-sm font-semibold text-green-800 mb-3">Categories</h4>
                        <div className={`grid ${colsClass} gap-2 text-xs`}>
                          {activeCategories.map((categoryName) => {
                            const displayInfo = getCategoryDisplayInfo(categoryName);
                            if (!displayInfo) return null;
                            const IconComponent = displayInfo.icon;
                            return (
                              <div key={categoryName} className="text-center">
                                <IconComponent className={`w-4 h-4 ${displayInfo.iconColor} mx-auto mb-1`} />
                                <div className={`${displayInfo.labelColor} font-medium`}>{categoryName}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
  
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {questions.map((question, index) => {
                    const categoryInfo = getESGCategoryInfo(question.disclosureTopic, question.sustainableDimension);
                    const CategoryIcon = categoryInfo.icon;
  
                    return (
                      <button
                        key={question.id}
                        data-question-id={question.id}
                        onClick={() => setCurrentQuestionIndex(index)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                          index === currentQuestionIndex
                            ? `bg-gradient-to-r ${categoryInfo.bgColor} ${categoryInfo.borderColor} ${categoryInfo.textColor} shadow-md`
                          : answers[question.id]
                              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 text-green-800 hover:from-green-100 hover:to-emerald-100 shadow-sm'
                            : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 text-gray-700 hover:from-gray-100 hover:to-slate-100'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <CategoryIcon className={`w-4 h-4 text-${categoryInfo.color}-600`} />
                            {question.section9_1 && (
                              <span title={t('questionsManagement.section91Feature')} className="inline-flex">
                                <FaStar className="w-3.5 h-3.5 text-amber-500" aria-hidden />
                              </span>
                            )}
                            <span className="text-sm font-bold">Q{index + 1}</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium bg-${categoryInfo.color}-100 text-${categoryInfo.color}-700`}>
                              {categoryInfo.category}
                            </span>
                            {question.hasApiSupport && (
                              <span className="text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-700 border border-blue-300">
                                API
                              </span>
                            )}
                          </div>
                          {answers[question.id] ? (
                            <FaCheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed line-clamp-2">{getQuestionText(question)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
  
            {/* Enhanced Main Question Content */}
            <div className="lg:col-span-3">
              <div className={`relative bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-500 ${
                isTransitioning
                  ? 'opacity-0 scale-95 transform-gpu'
                  : 'opacity-100 scale-100'
              }`}>
                {/* Transition overlay for visual feedback */}
                {isTransitioning && (
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 opacity-20 animate-pulse z-10 flex items-center justify-center">
                    <div className="text-white font-bold text-xl animate-bounce">
{t('answers.loadingNextQuestion')}
                    </div>
                  </div>
                )}
                {/* Enhanced Question Header */}
                <div className={`bg-gradient-to-r ${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).bgColor} px-8 py-6 border-b border-gray-200 relative overflow-hidden`}>
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-2 left-2 w-8 h-8 border border-gray-400 rounded-full"></div>
                    <div className="absolute top-4 right-4 w-4 h-4 bg-gray-400 rounded-full"></div>
                    <div className="absolute bottom-2 left-8 w-6 h-6 border border-gray-400 rounded-full"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 bg-gradient-to-r from-${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).color}-100 to-${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).color}-200 rounded-xl flex items-center justify-center shadow-md`}>
                        {React.createElement(getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).icon, {
                            className: `w-6 h-6 text-${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).color}-600`
                        })}
                      </div>
                      <div>
                          <h2 className="text-2xl font-bold text-gray-800">
                          {t('answers.questionOf', { current: currentQuestionIndex + 1, total: questionCount })}
                        </h2>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold bg-${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).color}-100 text-${getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).color}-700`}>
                            {getESGCategoryInfo(questions[currentQuestionIndex]?.disclosureTopic, questions[currentQuestionIndex]?.sustainableDimension).category}
                          </span>
                            {questions[currentQuestionIndex]?.hasApiSupport && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300">
                                {t('answers.apiSupported')}
                              </span>
                            )}
                            {questions[currentQuestionIndex]?.section9_1 && (
                              <span title={t('questionsManagement.section91Feature')} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                <FaStar className="w-3 h-3 mr-1" aria-hidden />
                                9.1
                              </span>
                            )}
                            {questions[currentQuestionIndex]?.showAnswerStatusOptions !== false && (
                            <div className="flex items-center space-x-2">
                            {getAnswerTypeIcon(answerTypes[questions[currentQuestionIndex]?.id])}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAnswerTypeColor(answerTypes[questions[currentQuestionIndex]?.id])}`}>
                              {answerTypes[questions[currentQuestionIndex]?.id] === 'true' ? t('answers.yes') :
                                answerTypes[questions[currentQuestionIndex]?.id] === 'false' ? t('answers.no') :
                                  answerTypes[questions[currentQuestionIndex]?.id] === 'progress' ? t('answers.inProgress') : t('answers.notSet')}
                            </span>
                          </div>
                            )}
                        </div>
                      </div>
                    </div>
  
                      {/* ESG Tips Toggle */}
                    <button
                      onClick={() => setShowESGTips(!showESGTips)}
                        className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2 ${
                          showESGTips 
                            ? 'bg-green-600 text-white shadow-lg' 
                            : 'bg-white text-green-600 border-2 border-green-200 hover:bg-green-50'
                        }`}
                      >
                        <FaLightbulb className="w-4 h-4" />
                        <span>{t('answers.esgTips')}</span>
                    </button>
                  </div>
  
                    <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="font-semibold">{t('answers.topic')}</span>
                        <span className="ml-2 font-medium">{questions[currentQuestionIndex]?.disclosureTopic}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">{t('answers.industry')}</span>
                        <span className="ml-2 font-medium">{questions[currentQuestionIndex]?.industry}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold">{t('answers.code')}</span>
                        <span className="ml-2 font-medium">{questions[currentQuestionIndex]?.topicCode}</span>
                      </div>
                    </div>
                  </div>
                </div>
  
                {/* Question Content */}
                <div className="p-8">
                  {/* ESG Tips Panel */}
                  {showESGTips && (
                    <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 shadow-sm">
                      <div className="flex items-center mb-4">
                        <FaLightbulb className="w-6 h-6 text-yellow-600 mr-3" />
                        <h4 className="text-lg font-semibold text-yellow-800">ESG Best Practices</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {getESGTips(questions[currentQuestionIndex]?.disclosureTopic).map((tip, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <FaStar className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                            <p className="text-sm text-yellow-700 leading-relaxed">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
  
                  <h3 className="text-xl font-semibold text-gray-800 mb-8 leading-relaxed">
                    {getQuestionText(questions[currentQuestionIndex])}
                  </h3>
  
                  {/* Enhanced Answer Type Selection */}
                  {(() => {
                    const currentQ = questions[currentQuestionIndex];
                    const showStatus = currentQ?.showAnswerStatusOptions !== false;
                    // const hasSubQuestions = currentQ?.subQuestions && currentQ.subQuestions.length > 0; // DISABLED: Subquestions functionality disabled
                    
                    return (
                      <>
                        {showStatus && (
                        <div className="mb-8">
                          <label className="block text-lg font-semibold text-gray-700 mb-4 flex items-center">
                            <FaCrosshairs className="w-5 h-5 mr-2 text-green-600" />
{t('answers.answerStatus')}
                          </label>
                          <div className="grid grid-cols-3 gap-4">
                            {[
                              { value: 'true', label: t('answers.yes'), icon: FaCheckCircle, color: 'green', description: t('answers.fullyImplemented') },
                              { value: 'false', label: t('answers.no'), icon: FaTimesCircle, color: 'red', description: t('answers.notImplemented') },
                              { value: 'progress', label: t('answers.inProgress'), icon: FaClock, color: 'yellow', description: t('answers.underDevelopment') }
                            ].map(({ value, label, icon: Icon, color, description }) => (
                              <label key={value} className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-200 hover:shadow-md ${
                                answerTypes[questions[currentQuestionIndex]?.id] === value
                                  ? `bg-gradient-to-r from-${color}-50 to-${color}-100 border-${color}-300 text-${color}-800 shadow-lg`
                                  : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 text-gray-700 hover:from-gray-100 hover:to-slate-100'
                              }`}>
                                <input
                                  type="radio"
                                  className="sr-only"
                                  name={`answerType-${questions[currentQuestionIndex]?.id}`}
                                  value={value}
                                  checked={answerTypes[questions[currentQuestionIndex]?.id] === value}
                                  onChange={(e) => handleAnswerTypeChange(questions[currentQuestionIndex]?.id, e.target.value)}
                                />
                                <div className="text-center">
                                  <div className="flex justify-center mb-3">
                                    <Icon className={`w-8 h-8 text-${color}-500`} />
                                  </div>
                                  <div className="font-bold text-lg mb-1">{label}</div>
                                  <div className="text-sm opacity-75">{description}</div>
                                </div>
                              </label>
                            ))}
                          </div>

                          {/* Show message when answer is selected */}
                          {answerTypes[questions[currentQuestionIndex]?.id] && (
                            <div className={`mt-6 p-4 rounded-xl border-2 ${
                              answerTypes[questions[currentQuestionIndex]?.id] === 'true'
                                ? 'bg-green-50 border-green-200'
                                : answerTypes[questions[currentQuestionIndex]?.id] === 'false'
                                ? 'bg-red-50 border-red-200'
                                : 'bg-yellow-50 border-yellow-200'
                            }`}>
                              {answerTypes[questions[currentQuestionIndex]?.id] === 'true' ? (
                                <p className="text-green-700 text-sm">
                                  {/* DISABLED: Subquestions functionality disabled */}
                                  {/* {hasSubQuestions
                                    ? "After saving, you'll be asked to provide additional details."
                                    : t('answers.youCanSaveNow')} */}
                                  {t('answers.youCanSaveNow')}
                                </p>
                              ) : answerTypes[questions[currentQuestionIndex]?.id] === 'false' ? (
                                <p className="text-red-700 text-sm">
                                  Click "Save & Next" to proceed to the next question.
                                </p>
                              ) : (
                                <p className="text-yellow-700 text-sm">
                                  Click "Save & Next" to proceed to the next question.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        )}
  
                        {/* Enhanced Answer Text Area */}
                        <div className="mb-8">
                          <label className="block text-lg font-semibold text-gray-700 mb-4 flex items-center">
                            <FaBookOpen className="w-5 h-5 mr-2 text-blue-600" />
                            {currentQ?.answerInputKind === 'number'
                              ? t('answers.numericAnswerLabel')
                              : currentQ?.answerInputKind === 'fill_in_blanks'
                                ? t('answers.fillInBlanksLabel')
                                : t('answers.detailedAnswer')}
                          </label>
                          <div className="relative">
                            {currentQ?.answerInputKind === 'number' ? (
                              <input
                                type="number"
                                step="any"
                                className="w-full rounded-2xl border-2 border-gray-200 bg-gradient-to-r from-white to-gray-50 p-6 text-lg shadow-sm transition-all duration-200 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                                placeholder={t('answers.numericAnswerPlaceholder')}
                                value={answers[questions[currentQuestionIndex]?.id] ?? ''}
                                onChange={(e) =>
                                  handleAnswerChange(
                                    questions[currentQuestionIndex]?.id,
                                    e.target.value
                                  )
                                }
                              />
                            ) : currentQ?.answerInputKind === 'fill_in_blanks' ? (
                              <div className="w-full rounded-2xl border-2 border-gray-200 bg-gradient-to-r from-white to-gray-50 p-6 shadow-sm">
                                <FillInBlanksAnswerField
                                  template={currentQ.fillInBlanksTemplate || ''}
                                  value={
                                    answers[questions[currentQuestionIndex]?.id] ??
                                    '{"blanks":[],"units":[]}'
                                  }
                                  onChange={(json) =>
                                    handleAnswerChange(questions[currentQuestionIndex]?.id, json)
                                  }
                                  includeUnit={Boolean(currentQ.answerIncludeUnitDropdown)}
                                  units={currentQ.valueUnitFormat || []}
                                />
                              </div>
                            ) : (
                              <>
                                <textarea
                                  className="w-full p-6 border-2 border-gray-200 rounded-2xl bg-gradient-to-r from-white to-gray-50 shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 resize-none"
                                  placeholder="Provide a comprehensive answer with specific examples, metrics, and implementation details..."
                                  rows={8}
                                  value={answers[questions[currentQuestionIndex]?.id] || ''}
                                  onChange={(e) =>
                                    handleAnswerChange(
                                      questions[currentQuestionIndex]?.id,
                                      e.target.value
                                    )
                                  }
                                />
                                <div className="absolute bottom-4 right-4 text-xs text-gray-400">
                                  {(answers[questions[currentQuestionIndex]?.id] || '').length}{' '}
                                  characters
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {currentQ?.section9_1 && (
                          <div className="mb-8">
                            <label className="block text-base font-semibold text-gray-700 mb-2">
                              {t('answers.noAnswerReasonLabel')}
                            </label>
                            <p className="text-sm text-gray-500 mb-3">{t('answers.noAnswerReasonHint')}</p>
                            <textarea
                              className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-white shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 resize-y min-h-[100px]"
                              placeholder={t('answers.noAnswerReasonPlaceholder')}
                              rows={4}
                              value={noAnswerReasons[questions[currentQuestionIndex]?.id] ?? ''}
                              onChange={(e) =>
                                handleNoAnswerReasonChange(
                                  questions[currentQuestionIndex]?.id,
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Enhanced Action Buttons */}
                  <div className="flex flex-wrap gap-4 justify-between">
                    {/* Navigation */}
                    <div className="flex gap-4">
                      <button
                        className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg hover:from-gray-600 hover:to-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3"
                        onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
                        disabled={currentQuestionIndex === 0}
                      >
                        <FaChevronLeft className="w-5 h-5" />
                        <span>{t('answers.previous')}</span>
                      </button>
                      <button
                        className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3"
                        onClick={() => handleSaveAnswer(questions[currentQuestionIndex]?.id, questions[currentQuestionIndex]?.userQuestionsAssignment)}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" variant="light" />
                            <span>{t('answers.saving')}</span>
                          </>
                        ) : (
                          <>
                            <FaSave className="w-5 h-5" />
                            <span>{currentQuestionIndex === questions.length - 1 ? t('answers.completeAssessment') : t('answers.saveAndNext')}</span>
                          </>
                        )}
                      </button>
                    </div>
  
                    {/* AI Assistant Actions */}
                    <div className="flex gap-4">
                      <button
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center space-x-3"
                        onClick={() => {
                          const chatbotAnswer = localStorage.getItem("currentAnswer");
                          if (chatbotAnswer) {
                            const currentQuestionId = questions[currentQuestionIndex]?.id;
                            if (currentQuestionId) {
                              setAnswers((prev) => ({
                                ...prev,
                                [currentQuestionId]: chatbotAnswer,
                              }));
                              toastr.success(t('answers.aiResponseInserted'));
                            }
                          } else {
                            toastr.warning(t('answers.noAiResponse'));
                          }
                        }}
                      >
                        <FaRobot className="w-5 h-5" />
                        <span>{t('answers.useAiResponse')}</span>
                      </button>
  
                      <button
                        className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold rounded-xl shadow-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 flex items-center space-x-3"
                        onClick={() => setIsFileModalOpen(true)}
                      >
                        <FaFileUpload className="w-5 h-5" />
                        <span>{t('answers.addEsgDocuments')}</span>
                      </button>
  
                      {/* DISABLED: Subquestions functionality disabled */}
                      {/* <button
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-xl shadow-lg hover:from-purple-600 hover:to-indigo-600 transition-all duration-200 flex items-center space-x-3"
                        onClick={() => {
                          const currentQuestion = questions[currentQuestionIndex];
                          if (currentQuestion) {
                            setCurrentQuestionForSubQuestions(currentQuestion);
                            setShowSubQuestionsModal(true);
                          }
                        }}
                      >
                        <FaQuestionCircle className="w-5 h-5" />
                        <span>{t('answers.subQuestions')}</span>
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-32 h-32 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <FaLeaf className="w-16 h-16 text-green-600" />
            </div>
            <h3 className="text-3xl font-bold text-gray-700 mb-4">{t('answers.noEsgQuestionsAssigned')}</h3>
            <p className="text-gray-500 text-lg mb-8">You don't have any sustainability assessment questions assigned at the moment.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">Environmental</span>
              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Social</span>
              <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Governance</span>
            </div>
          </div>
        )}
  
        {/* Enhanced Chatbot Component */}
        <Chatbot />
      </div>
  
      {/* Enhanced ManageFile modal component */}
      <ManageFile isOpen={isFileModalOpen} onClose={() => setIsFileModalOpen(false)} />
  
      {/* DISABLED: Subquestions functionality disabled */}
      {/* <SubQuestionsModal
        question={currentQuestionForSubQuestions}
        isOpen={showSubQuestionsModal}
        onClose={() => setShowSubQuestionsModal(false)}
        onSave={handleSubQuestionsSave}
      /> */}
  
      <ApiHelpOfferModal
        question={currentQuestionForApi}
        isOpen={showApiHelpOfferModal}
        onClose={() => setShowApiHelpOfferModal(false)}
        onAccept={handleApiHelpAccept}
        onDecline={handleApiHelpDecline}
      />

      <ApiQuestionsModal
        question={currentQuestionForApi}
        isOpen={showApiQuestionsModal}
        onClose={() => setShowApiQuestionsModal(false)}
        onSave={handleApiQuestionsSave}
      />
      
      {/* Completion Celebration Modal - Only show when ALL questions are actually answered AND last answer is saved */}
      {showCompletionModal && questionCount > 0 ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md mx-4 shadow-2xl text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaRocket className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{t('answers.congratulations')}</h2>
            <p className="text-lg text-gray-600 mb-6">{t('answers.assessmentCompletedWithScore')} <span className="font-bold text-green-600">{esgScore}%</span></p>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">{t('answers.environmental')}</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">{t('answers.social')}</span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">{t('answers.governance')}</span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200"
            >
{t('answers.viewResults')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AnswersToQuestions;
